import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { MessageThreadType } from "gql/graphql";
import type Quill from "quill";
import type { Op } from "quill/core";
import type { Delta } from "quill";
import useQuillMentions, { QuillMention } from "./mentions/use-quill-mentions";
import {
  DISTRIBUTION_GROUP_MARKDOWN,
  MENTION_TYPE,
  TAG_MARKDOWN,
  USER_MARKDOWN,
} from "modules/utils/Constants";
import { quillAutolinkUrl } from "./quill-autolink-url";

export type QuillListFormats = "ordered" | "bullet";

export interface IQuillFormats {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  header?: number | false;
  list?: QuillListFormats;
  blockquote?: boolean;
  "code-block"?: boolean;
  link?: string;
  color?: string | string[];
  background?: string | string[];
  mention?: boolean;
}

export interface IClickedMention {
  id: string;
  type: MENTION_TYPE;
  top: number;
  left: number;
  width: number;
  height: number;
}

interface IProps {
  readOnly?: boolean;
  defaultValue?: string | null;
  placeholder?: string;
  onChange?: (delta: string, plainText: string, lastChar: string) => void;
  isReady?: boolean;
  onEnter?: () => void;
  onCtrlEnter?: () => void;
  autoFocus?: boolean;
  isLocked?: boolean;
  type: MessageThreadType;
  accessUserIdList?: string[];
  distributionGroupIdList?: string[];
  onFilePaste?: (files: File[]) => void;
}

const useQuill = ({
  readOnly,
  defaultValue,
  placeholder,
  onChange,
  isReady = true,
  onEnter,
  onCtrlEnter,
  autoFocus = true,
  isLocked,
  type,
  accessUserIdList,
  distributionGroupIdList,
  onFilePaste,
}: IProps) => {
  const [activeFormats, setActiveFormats] = useState<IQuillFormats>({});
  const [clickedMention, setClickedMention] = useState<IClickedMention | null>(
    null,
  );
  const { mentionOption } = useQuillMentions({
    isLocked,
    type,
    accessUserIdList,
    distributionGroupIdList,
  });

  const quillRef = useRef<Quill | null>(null);
  const defaultValueRef = useRef(defaultValue);
  const onChangeRef = useRef(onChange);
  const placeholderRef = useRef(placeholder);
  const autoFocusRef = useRef(autoFocus);
  const onEnterRef = useRef(onEnter);
  const onCtrlEnterRef = useRef(onCtrlEnter);
  const mentionOptionRef = useRef(mentionOption);
  const onFilePasteRef = useRef(onFilePaste);
  const pendingFocusRef = useRef(false);

  useLayoutEffect(() => {
    mentionOptionRef.current = mentionOption;
    onChangeRef.current = onChange;
    placeholderRef.current = placeholder;
    defaultValueRef.current = defaultValue;
    autoFocusRef.current = autoFocus;
    onEnterRef.current = onEnter;
    onCtrlEnterRef.current = onCtrlEnter;
    onFilePasteRef.current = onFilePaste;
  });

  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainer(node);
  }, []);

  const [toolbarContainer, setToolbarContainer] =
    useState<HTMLDivElement | null>(null);
  const toolbarRef = useCallback((node: HTMLDivElement | null) => {
    setToolbarContainer(node);
  }, []);

  const getPlainText = useCallback((quill: Quill) => {
    const delta = quill.getContents();
    let result = "";

    delta.ops?.forEach((op) => {
      const insert = op.insert as any;
      if (typeof insert === "string") {
        result += insert;
      } else if (insert?.mention) {
        const mention: QuillMention = insert.mention;
        switch (mention.type) {
          case "USER":
            result += `{{${USER_MARKDOWN}=${mention.id}}}`;
            break;
          case "DISTRIBUTION_GROUP":
            result += `{{${DISTRIBUTION_GROUP_MARKDOWN}=${mention.id}}}`;
            break;
          case "TAG":
            result += `{{${TAG_MARKDOWN}=${mention.id}}}`;
            break;
          default:
            break;
        }
      }
    });

    return result;
  }, []);

  const initQuill = useCallback(
    async (container: HTMLDivElement, toolbarContainer: HTMLDivElement) => {
      //Quill accesses the DOM at import time, so we lazy-load it to avoid SSR crashes
      const { default: Quill } = await import("quill");
      await import("quill-mention/autoregister");
      await import("quill/dist/quill.core.css");
      await import("quill/dist/quill.snow.css");
      const { createCustomMentionBlot } = await import(
        "./mentions/mention-blot"
      );

      Quill.register(createCustomMentionBlot(Quill), true);

      const editorContainer = container.appendChild(
        container.ownerDocument.createElement("div"),
      );

      const quill = new Quill(editorContainer, {
        theme: "snow",
        placeholder: placeholderRef.current,
        modules: {
          toolbar: {
            container: toolbarContainer,
          },
          mention: mentionOptionRef.current,
          table: true,
        },
        formats: [
          "bold",
          "italic",
          "underline",
          "strike",
          "color",
          "background",
          "list",
          "link",
          "mention",
        ],
      });

      //I only want my own styles here. Applying quill styles messes with our design
      toolbarContainer.classList.remove("ql-toolbar", "ql-snow");

      // Strip text/background color from pasted content so it matches the
      // thread's styling; other formatting (bold, italic, lists, ...) is kept.
      // Colors applied via the toolbar are unaffected.
      quill.clipboard.addMatcher(Node.ELEMENT_NODE, (_node, delta) => {
        delta.ops?.forEach((op) => {
          if (op.attributes) {
            delete op.attributes.color;
            delete op.attributes.background;
          }
        });
        return delta;
      });

      if (defaultValueRef.current) {
        try {
          const parsed = JSON.parse(defaultValueRef.current);
          quill.setContents(parsed, "silent");
        } catch {
          // Invalid JSON — ignore
        }
      }

      quill.on("selection-change", (range) => {
        if (range) {
          setActiveFormats(quill.getFormat() as IQuillFormats);
        }
      });

      quill.on("text-change", (changeDelta: Delta) => {
        quillAutolinkUrl(quill, changeDelta);

        setActiveFormats(quill.getFormat() as IQuillFormats);
        const delta = quill.getContents();
        const text = getPlainText(quill);

        // Extract last inserted character from the change delta
        let lastChar = "";
        changeDelta.ops?.forEach((op: Op) => {
          if (typeof op.insert === "string") {
            lastChar = op.insert.slice(-1);
          }
        });

        onChangeRef.current?.(JSON.stringify(delta), text, lastChar);
      });

      const { registerKeyboardBindings } = await import(
        "./quill-keyboard-bindings"
      );
      await registerKeyboardBindings(quill, onEnterRef, onCtrlEnterRef);

      const onPaste = (e: ClipboardEvent) => {
        const files = Array.from(e.clipboardData?.files ?? []);
        if (files.length > 0) {
          e.preventDefault();
          onFilePasteRef.current?.(files);
        }
      };

      const onClick = (e: MouseEvent) => {
        const mention = (e.target as HTMLElement).closest(
          ".mention",
        ) as HTMLElement | null;
        if (mention) {
          const rect = mention.getBoundingClientRect();
          setClickedMention({
            id: mention.dataset.id ?? "",
            type: (mention.dataset.type as MENTION_TYPE) ?? "",
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });
          return;
        }
        setClickedMention(null);
      };

      quill.root.addEventListener("paste", onPaste);
      quill.root.addEventListener("click", onClick);

      if (autoFocusRef.current) {
        quill.root.focus();
        quill.setSelection(quill.getLength(), 0);
      }

      const cleanup = () => {
        quill.root.removeEventListener("paste", onPaste);
        quill.root.removeEventListener("click", onClick);
        quill.off("text-change");
        quill.off("selection-change");
      };

      if (pendingFocusRef.current) {
        quill.focus();
        quill.setSelection(quill.getLength(), 0);
        pendingFocusRef.current = false;
      }

      return { quill, cleanup };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [getPlainText],
  );

  useEffect(() => {
    quillRef.current?.enable(!readOnly);
  }, [readOnly]);

  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!container || !toolbarContainer || !isReady) return;

    let cancelled = false;

    initQuill(container, toolbarContainer).then(({ quill, cleanup }) => {
      if (cancelled) {
        cleanup();
        return;
      }
      quillRef.current = quill;
      cleanupRef.current = cleanup;
    });

    return () => {
      cancelled = true;
      cleanupRef.current?.();
      quillRef.current = null;
      container.innerHTML = "";
      //explicily remove mention list to prevent it from sticking around after unmounting
      const mentionList = document.querySelector(".ql-mention-list-container");
      if (mentionList) {
        mentionList.remove();
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, toolbarContainer, isReady, isLocked]);

  const focus = useCallback(() => {
    if (quillRef.current) {
      quillRef.current.focus();
      quillRef.current.setSelection(quillRef.current.getLength(), 0);
    } else {
      pendingFocusRef.current = true;
    }
  }, []);

  const reset = useCallback(() => {
    const quill = quillRef.current;
    if (!quill) return;
    quill.setContents([] as any, "silent");
    quill.history.clear();
  }, []);

  const appendText = useCallback((value: string) => {
    const quill = quillRef.current;
    if (!quill) return;
    const length = quill.getLength();
    quill.insertText(length - 1, value, "user");
    quill.setSelection(length, 0);
  }, []);

  const appendMention = useCallback((mention: QuillMention) => {
    const quill = quillRef.current;
    if (!quill) return;
    const length = quill.getLength();
    quill.insertEmbed(length - 1, "mention", mention, "user");
    quill.insertText(length, " ", "user");
    quill.setSelection(length + 1, 0);
  }, []);

  const format = useCallback((name: string, value: unknown) => {
    const quill = quillRef.current;
    if (!quill) return;
    quill.format(name, value);
    setActiveFormats(quill.getFormat() as IQuillFormats);
  }, []);

  const insertLink = useCallback((url: string) => {
    const quill = quillRef.current;
    if (!quill) return;
    const range = quill.getSelection(true);
    if (range && range.length > 0) {
      // Text is selected — format it as a link.
      quill.format("link", url);
    } else {
      // Nothing selected — insert the URL as its own link text.
      const index = range?.index ?? quill.getLength();
      quill.insertText(index, url, "link", url, "user");
      quill.setSelection(index + url.length, 0);
    }
    setActiveFormats(quill.getFormat() as IQuillFormats);
  }, []);

  const closeClickedMention = useCallback(() => {
    setClickedMention(null);
  }, []);

  return {
    containerRef,
    toolbarRef,
    focus,
    reset,
    appendText,
    appendMention,
    format,
    insertLink,
    activeFormats,
    clickedMention,
    closeClickedMention,
  };
};

export default useQuill;
